import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Button, HStack, ModalBody, ModalFooter, Spinner, Text, VStack } from '@bonusly/design-system';

import { useMarkSpotlightCreatorViewed } from '../../../queries/employee-spotlights';
import { EmployeeSpotlight } from '../../../schemas/spotlight';
import { SpotlightListItem } from './spotlight-list-item';
import { openSpotlight } from './spotlight-navigation';

interface GeneratedSpotlightsListProps {
	spotlights: EmployeeSpotlight[];
	isLoading: boolean;
	targetUserName?: string;
	onCreateNew: () => void;
	onAddToRecognition?: (spotlight: EmployeeSpotlight) => void;
}

export const GeneratedSpotlightsList: FC<GeneratedSpotlightsListProps> = ({ spotlights, isLoading, targetUserName, onCreateNew, onAddToRecognition }) => {
	const { t } = useTranslation('spotlight');
	const navigate = useNavigate();

	const { mutate: markCreatorViewed } = useMarkSpotlightCreatorViewed();

	const isSingle = spotlights.length === 1;
	const singleSpotlight = isSingle ? spotlights[0] : null;
	const isSingleCompleted = singleSpotlight?.status === 'completed';
	const hasGenerating = spotlights.some((s) => s.status === 'pending' || s.status === 'processing');

	const handleViewSpotlight = () => {
		if (!singleSpotlight || !isSingleCompleted) return;
		if (!singleSpotlight.creatorViewedAt) {
			markCreatorViewed(singleSpotlight.id);
		}
		openSpotlight(singleSpotlight.id, singleSpotlight.url, navigate, 'create_spotlight_modal');
	};

	return (
		<>
			<ModalBody>
				{isLoading ? (
					<Box textAlign="center" py={8}>
						<Spinner size="md" />
					</Box>
				) : spotlights.length === 0 ? (
					<Box textAlign="center" py={8}>
						<Text color="text.soft">{t('generatedSpotlightsModal.empty')}</Text>
					</Box>
				) : (
					<VStack spacing={3} align="stretch" data-testid="generated-spotlights-list">
						{spotlights.map((spotlight) => (
							<SpotlightListItem
								key={spotlight.id}
								spotlight={spotlight}
								targetUserName={targetUserName}
								onAddToRecognition={onAddToRecognition}
								variant={isSingle ? 'single' : 'default'}
							/>
						))}
					</VStack>
				)}
			</ModalBody>
			<ModalFooter>
				<HStack spacing={3} w="100%" justify="flex-end">
					<Button variant="outline" colorScheme="secondary" onClick={onCreateNew} isDisabled={hasGenerating} data-testid="create-spotlight-button">
						{t('generatedSpotlightsModal.createNew')}
					</Button>
					{isSingle && (
						<Button onClick={handleViewSpotlight} isDisabled={!isSingleCompleted} data-testid="view-spotlight-button">
							{t('generatedSpotlightsModal.viewSpotlight')}
						</Button>
					)}
				</HStack>
			</ModalFooter>
		</>
	);
};
